import { inject, Injectable, OnDestroy } from '@angular/core';
import { Router } from '@angular/router';
import { BehaviorSubject, Observable, Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { SprayRecordService } from './spray-record.service';
import { ToastService } from './toast.service';
import { WebSocketService } from './websocket.service';

/**
 * Payload shape for `spray-record.created` and `spray-record.updated`. The
 * Hub keeps the wire payload lean (id + a handful of fields) — clients
 * fetch the full record by id via the API on receipt to avoid sending
 * denormalized rows over the socket.
 */
export interface SprayRecordEventPayload {
  sprayRecordId: string;
  organizationId: string;
  fieldId: string | null;
  applicationDate: string | null;
  status: string;
  /** Profile id of whoever performed the change. Informational. */
  actorProfileId: string | null;
  /**
   * socket.io connection id of the client that initiated the mutation
   * (echoed back via `X-Client-Socket-Id`). Receivers compare it against
   * their own socket id and suppress only their own writes.
   */
  originSocketId: string | null;
  timestamp: string;
}

/** Payload for `spray-record.deleted`. */
export interface SprayRecordDeletedPayload {
  sprayRecordId: string;
  organizationId: string;
  actorProfileId: string | null;
  originSocketId: string | null;
  timestamp: string;
}

/**
 * Spray Record Real-time Service
 *
 * Listens for `spray-record.created`, `spray-record.updated`, and
 * `spray-record.deleted` events on the org room. Refreshes
 * `SprayRecordService` on every event so any open list/detail re-renders
 * with fresh data. Surfaces a toast unless this browser tab itself
 * initiated the change (matched via `originSocketId`) — different
 * environments for the same user (other tabs, mobile) still get toasted.
 *
 * Mounted via `providedIn: 'root'` so it lives once per session and
 * starts listening as soon as the WebSocket reaches `authenticated`.
 */
@Injectable({ providedIn: 'root' })
export class SprayRecordRealtimeService implements OnDestroy {
  private destroy$ = new Subject<void>();

  private createdSubject = new BehaviorSubject<SprayRecordEventPayload | null>(null);
  public created$: Observable<SprayRecordEventPayload | null> = this.createdSubject.asObservable();

  private updatedSubject = new BehaviorSubject<SprayRecordEventPayload | null>(null);
  public updated$: Observable<SprayRecordEventPayload | null> = this.updatedSubject.asObservable();

  private deletedSubject = new BehaviorSubject<SprayRecordDeletedPayload | null>(null);
  public deleted$: Observable<SprayRecordDeletedPayload | null> = this.deletedSubject.asObservable();

  private isSubscribed = false;

  private websocketService = inject(WebSocketService);
  private sprayRecordService = inject(SprayRecordService);
  private toastService = inject(ToastService);
  private router = inject(Router);

  constructor() {
    this.websocketService.connectionState$.pipe(takeUntil(this.destroy$)).subscribe(state => {
      if (state === 'authenticated' && !this.isSubscribed) {
        this.subscribeToEvents();
      } else if (state === 'disconnected') {
        this.isSubscribed = false;
      }
    });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private subscribeToEvents(): void {
    if (this.isSubscribed) return;
    this.isSubscribed = true;

    this.websocketService.on<SprayRecordEventPayload>('spray-record.created').pipe(
      takeUntil(this.destroy$),
    ).subscribe(event => this.handleCreated(event));

    this.websocketService.on<SprayRecordEventPayload>('spray-record.updated').pipe(
      takeUntil(this.destroy$),
    ).subscribe(event => this.handleUpdated(event));

    this.websocketService.on<SprayRecordDeletedPayload>('spray-record.deleted').pipe(
      takeUntil(this.destroy$),
    ).subscribe(event => this.handleDeleted(event));
  }

  private handleCreated(event: SprayRecordEventPayload): void {
    console.log('[SprayRecordRealtime] Received spray-record.created:', event);
    this.createdSubject.next(event);
    void this.sprayRecordService.loadRecords();
    if (this.isSelfOrigin(event.originSocketId)) return;
    this.toastService.show('A new spray record was added by a teammate.', {
      type: 'info',
      title: 'New Spray Record',
      icon: 'bi-droplet-half',
      action: {
        label: 'View',
        callback: () => this.router.navigate([
          '/logbook/spray-records',
          event.sprayRecordId,
          'view',
        ]),
      },
    });
  }

  private handleUpdated(event: SprayRecordEventPayload): void {
    console.log('[SprayRecordRealtime] Received spray-record.updated:', event);
    this.updatedSubject.next(event);
    void this.sprayRecordService.loadRecords();
    if (this.isSelfOrigin(event.originSocketId)) return;
    this.toastService.show('A spray record was updated by a teammate.', {
      type: 'info',
      title: 'Spray Record Updated',
      icon: 'bi-arrow-repeat',
      action: {
        label: 'View',
        callback: () => this.router.navigate([
          '/logbook/spray-records',
          event.sprayRecordId,
          'view',
        ]),
      },
    });
  }

  private handleDeleted(event: SprayRecordDeletedPayload): void {
    console.log('[SprayRecordRealtime] Received spray-record.deleted:', event);
    this.deletedSubject.next(event);
    void this.sprayRecordService.loadRecords();
    if (this.isSelfOrigin(event.originSocketId)) return;
    this.toastService.show('A spray record was deleted by a teammate.', {
      type: 'warning',
      title: 'Spray Record Removed',
      icon: 'bi-trash',
    });
  }

  /**
   * Suppress the toast when this tab itself initiated the change. The
   * Hub stamps the originating client's `socket.id` on every event
   * payload (via `X-Client-Socket-Id`); a match against our own socket
   * id means "we did this", so the user doesn't need a "your teammate
   * added a record" alert about their own action. The same user signed
   * in elsewhere (other tab, mobile) has a different socket id and
   * still gets the toast. The data refresh runs regardless.
   */
  private isSelfOrigin(originSocketId: string | null): boolean {
    if (!originSocketId) return false;
    const mine = this.websocketService.getSocketId();
    return !!mine && mine === originSocketId;
  }
}
